import React, { useContext } from 'react';
import { FC, useEffect, useState } from 'react';
import { getCooldownDuration } from '../businessLogicService/retakeCountdownHelper';

const STORAGE_KEY = 'skill-challenge-cooldowns';

type SkillCooldown = {
  skillCode: string;
  attempts: number;
  startedAt: number;
  durationMs: number;
};

const CooldownContext = React.createContext<{
  startCooldown: (skillCode: string, attempts: number) => void;
  getRemainingTime: (skillCode: string) => number;
  getTotalCooldownTime: (skillCode: string) => number;
  isBlocked: (skillCode: string) => boolean;
  remainingDone: (skillCode: string) => boolean;
  setRemainingDone: (skillCode: string) => void;
} | null>(null);

const getInitialCooldowns = (): Record<string, SkillCooldown> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return {};
    return JSON.parse(saved);
  } catch (error) {
    console.error('Failed to parse cooldowns from localStorage', error);
    localStorage.removeItem(STORAGE_KEY);
    return {};
  }
};

interface Props {
  children: React.ReactNode;
}

export const RetakeCountdownProvider: FC<Props> = ({ children }) => {
  const [_remaining, setRemaining] = useState<Record<string, boolean>>({});
  const [cooldowns, setCooldowns] = useState<Record<string, SkillCooldown>>(getInitialCooldowns);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cooldowns));
  }, [cooldowns]);

  const startCooldown = (skillCode: string, attempts: number) => {
    const durationMs = getCooldownDuration(attempts);

    setCooldowns(prev => ({
      ...prev,
      [skillCode]: {
        skillCode,
        attempts,
        startedAt: Date.now(),
        durationMs,
      },
    }));
  };

  const setRemainingDone = (skillCode: string) => {
    setRemaining(prev => ({ ...prev, [skillCode]: true }));
  };

  const remainingDone = (skillCode: string) => {
    const cd = cooldowns[skillCode];
    if (!cd) return true; // si no hay cooldown registrado, asumimos que se puede intentar
    const elapsed = Date.now() - cd.startedAt;
    return elapsed >= cd.durationMs;
  };

  const getRemainingTime = (skillCode: string) => {
    const cd = cooldowns[skillCode];
    if (!cd) return 0;

    const elapsed = Date.now() - cd.startedAt;
    return Math.max(cd.durationMs - elapsed, 0);
  };

  const getTotalCooldownTime = (skillCode: string) => {
    const cd = cooldowns[skillCode];
    return cd ? cd.durationMs : 0;
  };

  const isBlocked = (skillCode: string) => {
    return getRemainingTime(skillCode) > 0;
  };

  return (
    <CooldownContext.Provider
      value={{
        startCooldown,
        getRemainingTime,
        getTotalCooldownTime,
        isBlocked,
        remainingDone,
        setRemainingDone,
      }}
    >
      {children}
    </CooldownContext.Provider>
  );
};

export const useCooldown = () => {
  return useContext(CooldownContext);
};
